// sampleAndOrExpression — canned Include-zone tree used to seed the platform
// intro tour when the user lands on an empty canvas. Mirrors the design handoff's
// SAMPLE_TREE shape so every tour step has a real DOM anchor.
//
// Tree:
//   AND
//     ├─ State IN [KS, MO, IA]
//     ├─ Age 25–65
//     └─ OR
//          ├─ Marital Status = Married Now
//          └─ Gender = Female
//
// We hand-pick attributes that exist in the data dictionary (so
// getAttributeById doesn't fail) and that exercise three different component
// types — multi-select (state), range (age), boolean (marital, gender) — so
// the seeded canvas reflects the variety of cards a real audience uses.
//
// Pre-populated `state` values give the PlainLanguageSummary something
// substantive to render ("State: KS, MO, IA"). If the dictionary shape ever
// drifts, leafFromSeed throws loudly — the tour will fall back to running
// against an empty canvas, which is preferable to a silent miss-match.

import dataDictionary from '../../assets/data-dictionary/dataDictionary.json';
import { getAttributeById } from '../../helpers/dataDictionary';
import { createGroup, createLeaf } from '../../helpers/expressionTree';
import type {
  ExpressionGroup,
  VariableLeaf,
  VariableState,
} from '../../types/dataDictionary';

type Seed = {
  id: string;
  state: VariableState['state'];
};

function leafFromSeed({ id, state }: Seed): VariableLeaf {
  const attr = getAttributeById(dataDictionary, id) as
    | VariableState
    | undefined;
  if (!attr) {
    throw new Error(
      `[platformIntroTour] sample seed references missing attribute id: ${id}`,
    );
  }
  // Clone the dictionary entry so we don't mutate the shared JSON import,
  // then layer the seeded state on top.
  return createLeaf({ ...attr, state });
}

/**
 * Build the canned AND/OR expression used to seed the Include zone when the
 * tour starts on an empty canvas.
 */
export function buildAndOrTourSampleTree(): ExpressionGroup {
  const stateLeaf = leafFromSeed({
    id: 'Geography___state',
    state: { values: ['KS', 'MO', 'IA'] },
  });
  const ageLeaf = leafFromSeed({
    id: 'Demographics___Individual___Age and Birthday___Age',
    state: { min: 25, max: 65 },
  });
  const marriedLeaf = leafFromSeed({
    id: 'Demographics___Individual___Marital Status___Married Now',
    state: null,
  });
  const femaleLeaf = leafFromSeed({
    id: 'Demographics___Individual___Gender___Female',
    state: null,
  });

  const orGroup = createGroup('or', [marriedLeaf, femaleLeaf]);
  return createGroup('and', [stateLeaf, ageLeaf, orGroup]);
}
